import React, { useState, useEffect, useCallback, useMemo } from 'react';

import dayjs from 'dayjs';

import { Link, useSearchParams } from 'react-router-dom';

import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Space,
  Tag,
  Input,
  Select,
  Tabs,
  Form,
  Checkbox,
  DatePicker,
  notification,
} from 'antd';
import {

  ClockCircleOutlined,

  CheckCircleOutlined,

  PlusOutlined,

  HistoryOutlined,

  DashboardOutlined,

  SettingOutlined,

  UserOutlined,

  DownOutlined,

  CloseOutlined,

} from '@ant-design/icons';

import { useAuth } from '../contexts/AuthContext';

import { Resource, Order } from '../types';

import api from '../lib/api';

import { formatDateInTimezone, getTimezoneOptions } from '../lib/timezones';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';
import { formatMoney } from '../lib/currency';



const { Title: AntTitle } = Typography;

const { TabPane } = Tabs;



// Types for Resource with options

interface ResourceOption {

  id: number;

  resource_id: number;

  name: string;

  type: string;

  value: string;

  price_delta: number;

  is_free: boolean;

}



interface ResourceWithOptions {

  id: number;

  name: string;

  type: string;

  location?: string;

  gpu_model?: string;

  vcpus?: number;

  ram?: string;

  ssd?: string;

  os?: string;

  networking?: string;

  system_disk?: string;

  data_disk?: string;

  base_price_per_hour: number;

  available: boolean;

  options: {

    gpu: ResourceOption[];

    vcpu: ResourceOption[];

    ram: ResourceOption[];

    storage: ResourceOption[];

    os: ResourceOption[];

    location?: ResourceOption[];

    networking?: ResourceOption[];

    system_disk?: ResourceOption[];

    data_disk?: ResourceOption[];

  };

  defaultConfig: {

    gpu?: string;

    vcpu?: string;

    ram?: string;

    storage?: string;

    os?: string;

    location?: string;

    networking?: string;

    system_disk?: string;

    data_disk?: string;

  };

}



// ==================== Product Selection Component ====================

const ProductSelection: React.FC<{ onSelect: (resource: ResourceWithOptions) => void }> = ({ onSelect }) => {
  const { formatFromEur } = useCurrency();

  const [resources, setResources] = useState<ResourceWithOptions[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [activeType, setActiveType] = useState<'desktop' | 'server'>('desktop');



  useEffect(() => {

    const loadResources = async () => {

      try {

        setLoading(true);

        const response = await api.get<{ data: ResourceWithOptions[] }>('/api/resources');

        const data = response.data?.data || response.data || [];

        setResources(data);

      } catch (err: any) {

        console.error('Failed to load resources:', err);

        setError('Failed to load products. Please try again.');

      } finally {

        setLoading(false);

      }

    };

    loadResources();

  }, []);



  const filteredResources = resources

    .filter((r) => r.type === activeType)

    .sort((a, b) => Number(b.available) - Number(a.available));

  const hasDesktop = resources.some((r) => r.type === 'desktop');

  const hasServer = resources.some((r) => r.type === 'server');

  const showTypeSwitcher = hasDesktop && hasServer;



  useEffect(() => {

    if (!hasDesktop && hasServer && activeType !== 'server') {

      setActiveType('server');

    } else if (!hasServer && hasDesktop && activeType !== 'desktop') {

      setActiveType('desktop');

    }

  }, [hasDesktop, hasServer, activeType]);



  if (error) {

    return (

      <div className="text-center py-12">

        <div className="glass glass-hover border border-red-500/30 rounded-lg p-6 max-w-md mx-auto">

          <p className="text-red-400">{error}</p>

          <button

            onClick={() => window.location.reload()}

            className="mt-4 px-4 py-2 bg-gradient-to-r from-[#9FF13E] to-[#86D82E] rounded-lg text-white font-semibold"

          >

            Refresh

          </button>

        </div>

      </div>

    );

  }



  if (loading) {

    return (

      <div className="text-center py-12">

        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9FF13E] mx-auto"></div>

        <p className="mt-4 text-gray-400">Loading products...</p>

      </div>

    );

  }



  if (resources.length === 0) {

    return (

      <div className="text-center py-12">

        <p className="text-gray-400">No products available right now.</p>

      </div>

    );

  }



  const getDefaultOptionValue = (resource: ResourceWithOptions, type: keyof ResourceWithOptions['options']) => {

    const list = resource.options?.[type];

    const preferred = resource.defaultConfig?.[type as keyof ResourceWithOptions['defaultConfig']];



    if (!list || list.length === 0) {

      return preferred || '—';

    }



    return list.find((opt) => opt.value === preferred)?.value

      || list.find((opt) => opt.is_free)?.value

      || list[0].value;

  };



  return (

    <div className="dashboard-create-catalog">

      {showTypeSwitcher && (

        <div className="pricing-tabs xp-plan-toggle dashboard-create-toggle">

          <button

            className={`pricing-tab ${activeType === 'desktop' ? 'active' : ''}`}

            onClick={() => setActiveType('desktop')}

          >

            <i className="fas fa-desktop"></i>

            Cloud PC

          </button>

          <button

            className={`pricing-tab ${activeType === 'server' ? 'active' : ''}`}

            onClick={() => setActiveType('server')}

          >

            <i className="fas fa-server"></i>

            Cloud VPS

          </button>

        </div>

      )}



      <div className={`pricing-grid dashboard-pricing-grid ${filteredResources.length === 1 ? 'single-card' : ''}`}>

        {filteredResources.map((resource) => (

          <div

            key={resource.id}

            className={`pricing-card glass dashboard-pricing-card ${resource.available ? '' : 'resource-unavailable'} ${

              resource.available ? 'is-selectable' : ''

            }`}

            onClick={() => resource.available && onSelect(resource)}

          >

            <div className="pricing-header">

              <h3 className="pricing-name">{resource.name}</h3>

              <span className={`pricing-badge ${resource.available ? '' : 'unavailable'}`}>

                {resource.available ? 'Ready to launch' : 'Unavailable'}

              </span>

            </div>



            <div className="pricing-specs-grid">

              {resource.type !== 'server' ? (

                <>

                  <div className="spec-item">

                    <span className="spec-label">GPU</span>

                    <span className="spec-value">{resource.gpu_model || getDefaultOptionValue(resource, 'gpu')}</span>

                  </div>

                  <div className="spec-item">

                    <span className="spec-label">CPU and RAM</span>

                    <span className="spec-value">{resource.vcpus || getDefaultOptionValue(resource, 'vcpu')}</span>

                  </div>

                  <div className="spec-item">

                    <span className="spec-label">Storage</span>

                    <span className="spec-value">{resource.ssd || getDefaultOptionValue(resource, 'storage')}</span>

                  </div>

                  <div className="spec-item">

                    <span className="spec-label">OS</span>

                    <span className="spec-value">{resource.os || getDefaultOptionValue(resource, 'os')}</span>

                  </div>

                </>

              ) : (

                <>

                  <div className="spec-item">

                    <span className="spec-label">vCPU</span>

                    <span className="spec-value">{resource.vcpus || getDefaultOptionValue(resource, 'vcpu')}</span>

                  </div>

                  <div className="spec-item">

                    <span className="spec-label">RAM</span>

                    <span className="spec-value">{resource.ram || getDefaultOptionValue(resource, 'ram')}</span>

                  </div>

                  <div className="spec-item">

                    <span className="spec-label">Networking</span>

                    <span className="spec-value">{resource.networking || getDefaultOptionValue(resource, 'networking')}</span>

                  </div>

                  <div className="spec-item">

                    <span className="spec-label">System Disk</span>

                    <span className="spec-value">{resource.system_disk || getDefaultOptionValue(resource, 'system_disk')}</span>

                  </div>

                </>

              )}

            </div>



            <div className="pricing-footer">

              <div className="pricing-price">

                <div className="price-from">from</div>

                <div className="price-amount">

                  {formatFromEur(parseFloat(String(resource.base_price_per_hour || 0)))}

                </div>

                <div className="price-period">/hour</div>

                <div className="price-vat-note">Prices include VAT</div>

              </div>

              <button

                className={`btn w-100 ${resource.available ? 'btn-primary' : 'btn-outline-primary'}`}

                disabled={!resource.available}

              >

                {resource.available ? 'Configure Machine' : 'Not Available'}

              </button>

            </div>

          </div>

        ))}

      </div>

    </div>

  );

};



// ==================== Machine Configurator Component ====================

const MachineConfigurator: React.FC<{

  resource: ResourceWithOptions;

  onBack: () => void;

  userTimezone: string;

  systemTimezone: string;

}> = ({ resource, onBack, userTimezone, systemTimezone }) => {

  const MIN_START_DELAY_HOURS = 8;

  const BUSINESS_TIMEZONE = systemTimezone || process.env.REACT_APP_BUSINESS_TIMEZONE || 'UTC';

  const USER_TIMEZONE = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  const [totalPrice, setTotalPrice] = useState(0);

  const [durationHours, setDurationHours] = useState(1);

  const [durationMinutes, setDurationMinutes] = useState(0);

  const [startTime, setStartTime] = useState('');

  const [processing, setProcessing] = useState(false);

  const [licenseAccepted, setLicenseAccepted] = useState(false);
  const { currency, formatFromEur, convertFromEur } = useCurrency();



  // Initialize with default config

  useEffect(() => {

    const defaults: Record<string, string> = {};

    const options = resource.options || {};



    const pickDefault = (list?: ResourceOption[], preferred?: string) => {

      if (!list || list.length === 0) return undefined;

      return list.find(o => o.value === preferred)

        || list.find(o => o.is_free)

        || list[0];

    };



    const defaultGpu = pickDefault(options.gpu, resource.defaultConfig?.gpu);

    if (defaultGpu) defaults.gpu = defaultGpu.value;



    const defaultVcpu = pickDefault(options.vcpu, resource.defaultConfig?.vcpu);

    if (defaultVcpu) defaults.vcpu = defaultVcpu.value;



    const defaultRam = pickDefault(options.ram, resource.defaultConfig?.ram);

    if (defaultRam && resource.type === 'server') defaults.ram = defaultRam.value;



    const defaultStorage = pickDefault(options.storage, resource.defaultConfig?.storage);

    if (defaultStorage) defaults.storage = defaultStorage.value;



    const defaultOs = pickDefault(options.os, resource.defaultConfig?.os);

    if (defaultOs) defaults.os = defaultOs.value;



    const defaultLocation = pickDefault(options.location, resource.defaultConfig?.location);

    if (defaultLocation) defaults.location = defaultLocation.value;



    const defaultNetworking = pickDefault(options.networking, resource.defaultConfig?.networking);

    if (defaultNetworking) defaults.networking = defaultNetworking.value;



    const defaultSystemDisk = pickDefault(options.system_disk, resource.defaultConfig?.system_disk);

    if (defaultSystemDisk) defaults.system_disk = defaultSystemDisk.value;



    const defaultDataDisk = pickDefault(options.data_disk, resource.defaultConfig?.data_disk);

    if (defaultDataDisk) defaults.data_disk = defaultDataDisk.value;

    

    setSelectedOptions(defaults);

  }, [resource]);



  // Calculate price when options change

  useEffect(() => {

    let base = parseFloat(String(resource.base_price_per_hour || 0)) || 0;

    let optionsSum = 0;

    const options = resource.options || {};



    Object.entries(selectedOptions).forEach(([type, value]) => {

      const optList = options[type as keyof typeof options];

      const option = optList?.find(o => o.value === value);

      if (option) {

        optionsSum += parseFloat(String(option.price_delta)) || 0;

      }

    });



    setTotalPrice(base + optionsSum);

  }, [selectedOptions, resource]);



  const handleOptionChange = (type: string, value: string) => {

    setSelectedOptions(prev => ({ ...prev, [type]: value }));

  };



  const getSelectedOption = useCallback((type: string) => {
    const options = resource.options || {};
    const list = options[type as keyof typeof options] as ResourceOption[] | undefined;
    const currentValue = selectedOptions[type] || list?.[0]?.value;
    return list?.find((opt) => opt.value === currentValue);
  }, [resource.options, selectedOptions]);



  const isWindowsByolSelected = useCallback(() => {
    const option = getSelectedOption('os');
    const haystack = `${option?.value || ''} ${option?.name || ''}`.toLowerCase();
    if (!haystack.includes('windows 10')) return false;
    return haystack.includes('bring') || haystack.includes('byol') || haystack.includes('own');
  }, [getSelectedOption]);

  useEffect(() => {
    if (!isWindowsByolSelected()) {
      setLicenseAccepted(false);
    }
  }, [resource, selectedOptions, isWindowsByolSelected]);


  const getMinStartDate = () => new Date(Date.now() + MIN_START_DELAY_HOURS * 60 * 60 * 1000);



  const getMinStartTimeValue = () => {

    const minStart = getMinStartDate();

    const timezoneOffset = minStart.getTimezoneOffset() * 60000;

    return new Date(minStart.getTime() - timezoneOffset).toISOString().slice(0, 16);

  };



  const formatInTimezone = (date: Date, timeZone: string) => {

    try {

      return new Intl.DateTimeFormat('ru-RU', {

        timeZone,

        year: 'numeric',

        month: '2-digit',

        day: '2-digit',

        hour: '2-digit',

        minute: '2-digit',

        hour12: false,

      }).format(date);

    } catch {

      return date.toLocaleString();

    }

  };



  const minStartDate = getMinStartDate();

  const minStartTimeValue = getMinStartTimeValue();

  const minStartLocalLabel = formatInTimezone(minStartDate, USER_TIMEZONE);

  const minStartBusinessLabel = formatInTimezone(minStartDate, BUSINESS_TIMEZONE);



  const clampStartTimeValue = (value: string, showFeedback = true) => {

    if (!value) return value;

    const selected = new Date(value);

    if (Number.isNaN(selected.getTime())) return minStartTimeValue;

    const minStart = getMinStartDate();

    if (selected < minStart) {

      if (showFeedback) {

        notification.warning({

          message: `Minimum start is +${MIN_START_DELAY_HOURS} hours`,

          description: `Local: ${minStartLocalLabel} (${USER_TIMEZONE}) | Business: ${minStartBusinessLabel} (${BUSINESS_TIMEZONE})`,

        });

      }

      return minStartTimeValue;

    }

    return value;

  };



  const getDurationInHours = () => {

    const hrs = Number(durationHours) || 0;

    const mins = Number(durationMinutes) || 0;

    return hrs + mins / 60;

  };



  const calculateTotal = () => (totalPrice * getDurationInHours()).toFixed(2);



  const buildOrderItems = () => {

    const options = resource.options || {};



    return Object.entries(selectedOptions)

      .map(([type, value]) => {

        const optList = options[type as keyof typeof options];

        const option = optList?.find(o => o.value === value);

        if (!option) return null;



        return {

          name: option.name || type.toUpperCase(),

          value: option.value,

          price_delta: parseFloat(String(option.price_delta)) || 0,

          is_free: !!option.is_free,

        };

      })

      .filter(

        (

          item,

        ): item is {

          name: string;

          value: string;

          price_delta: number;

          is_free: boolean;

        } => item !== null,

      );

  };



  const handleOrder = async () => {

    setProcessing(true);

    try {

      const options = resource.options || {};

      const requiredTypes = resource.type !== 'server'

        ? ['location', 'vcpu', 'gpu', 'storage', 'os']

        : ['vcpu', 'ram', 'networking', 'system_disk', 'data_disk'];



      for (const type of requiredTypes) {

        const list = options[type as keyof typeof options];

        if (Array.isArray(list) && list.length > 0 && !selectedOptions[type]) {

          notification.error({ message: 'Please select all available options' });

          return;

        }

      }



      if (resource.type !== 'server' && isWindowsByolSelected() && !licenseAccepted) {

        notification.error({ message: 'Please confirm the Windows 10 license agreement' });

        return;

      }



      const duration = getDurationInHours();

      if (!duration || duration <= 0) {

        notification.error({ message: 'Please select a valid duration' });

        return;

      }



      const start = startTime ? new Date(startTime) : new Date();

      if (Number.isNaN(start.getTime())) {

        notification.error({ message: 'Please select a valid start time' });

        return;

      }



      const minStart = new Date(Date.now() + MIN_START_DELAY_HOURS * 60 * 60 * 1000);

      if (start < minStart) {

        notification.error({ message: `Start time must be at least ${MIN_START_DELAY_HOURS} hours from now` });

        return;

      }



      const end = new Date(start.getTime() + duration * 60 * 60 * 1000);

      const orderItems = buildOrderItems();



      const orderResponse = await api.post('/api/orders', {

        resource_id: resource.id,

        start_time: start.toISOString(),

        end_time: end.toISOString(),

        timezone: USER_TIMEZONE,

        items: orderItems,
        currency,

      });



      const backendTotal = parseFloat(String(orderResponse.data?.order?.total_cost));

      const amountForPayment = Number.isFinite(backendTotal)

        ? backendTotal.toFixed(2)

        : convertFromEur(parseFloat(calculateTotal())).toFixed(2);



      const paymentResponse = await api.post('/api/payments/create-payment', {

        userId: orderResponse.data.order.user_id,

        orderId: orderResponse.data.order.id,

        amount: amountForPayment,

        currency,
        frontOrigin: window.location.origin,

        description: `Order ${orderResponse.data.order.order_number}`,

      });



        if (paymentResponse.data.url) {
          window.location.href = paymentResponse.data.url;
        } else {
          window.location.href = '/payment/error';
        }
      } catch (err) {
        console.error('Order error:', err);
        window.location.href = '/payment/error';
      } finally {
        setProcessing(false);
      }
  };



  const getOptionLabel = (type: string, value: string): string => {

    const options = resource.options || {};

    const optList = options[type as keyof typeof options];

    const option = optList?.find(o => o.value === value);

    return option?.name || value;

  };



  const getOptionTypeLabel = (type: string): string => {

      const labels: Record<string, string> = {

        gpu: resource.type !== 'server' ? 'Streaming Quality' : 'GPU',

        vcpu: resource.type !== 'server' ? 'CPU and RAM' : 'vCPU',

        ram: 'RAM',

      storage: 'Storage',

      os: 'OS',

      location: 'Location',

      datacenter: 'Datacenter',

      networking: 'Networking',

      system_disk: 'System Disk',

      data_disk: 'Data Disk',

    };

    return labels[type] || type;

  };



  const getOptionPrice = (type: string, value: string): { price: number; isFree: boolean } => {

    const options = resource.options || {};

    const optList = options[type as keyof typeof options];

    const option = optList?.find(o => o.value === value);

    if (!option) return { price: 0, isFree: true };

    return { price: parseFloat(String(option.price_delta)) || 0, isFree: option.is_free };

  };



  const formatOptionDelta = (option: ResourceOption): string => {

    if (option.is_free) return 'Free';

    const delta = parseFloat(String(option.price_delta)) || 0;

    return `+${formatFromEur(delta)}`;

  };



  const stripOptionPrefix = (type: string, rawLabel: string): string => {

    const prefixes: Record<string, string[]> = {

      gpu: ['gpu', 'gpu model', 'gpumodel'],

      vcpu: ['vcpu', 'v cpu', 'virtual cpu', 'vcpucpu'],

      ram: ['ram', 'memory'],

      storage: ['storage', 'disk', 'ssd'],

      os: ['operating system', 'operatingsystem', 'os'],

      location: ['location', 'datacenter', 'data center'],

      networking: ['networking', 'network', 'bandwidth'],

      system_disk: ['system disk', 'system', 'disk'],

      data_disk: ['data disk', 'data', 'disk'],

    };

    let clean = (rawLabel || '').trim();

    clean = clean.replace(/^\s*\/+/, '').trim();

    const variants = prefixes[type] || [];



    for (const prefix of variants) {

      const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const re = new RegExp(`^${escaped}\\s*:?\\s*`, 'i');

      if (re.test(clean)) {

        clean = clean.replace(re, '').trim();

      }

    }



    if (type === 'location') {

      clean = clean.replace(/(location|datacenter|data\s*center)\s*:?/gi, '').trim();

      clean = clean.replace(/^[\s/:-]+/, '').trim();
    }

    return clean.replace(/^model\s*:?\s*/i, '').replace(/\s{2,}/g, ' ').trim();

  };



  const getCleanOptionLabel = (type: string, value: string): string => {

    const rawLabel = getOptionLabel(type, value);

    return stripOptionPrefix(type, rawLabel);

  };



  const getOptionDisplayLabel = (type: string, option: ResourceOption): string => {

    const name = stripOptionPrefix(type, option.name);

    const price = formatOptionDelta(option);

    return option.is_free ? `${name} · ${price}` : `${name} · ${price}`;

  };



  const renderOptionSelect = (type: string, label: string, options: ResourceOption[]) => {

    if (!options || options.length === 0) return null;

    const currentValue = selectedOptions[type] || options[0].value;

    return (

      <Select

        value={currentValue}

        onChange={(value) => handleOptionChange(type, value)}

        disabled={!resource.available}

        className="config-option-select"

        popupClassName="config-option-dropdown"

        suffixIcon={<DownOutlined className="config-option-caret" />}

        options={options.map((option) => ({

          value: option.value,

          label: getOptionDisplayLabel(type, option),

        }))}

      />

    );

  };



  const resourceTypeLabel =

    resource.type === 'desktop' ? 'Cloud PC' : resource.type === 'server' ? 'Cloud VPS' : resource.type;



    return (

      <div className={`machine-configurator ${resource.type !== 'server' ? 'config-dropdown-only' : ''}`}>

      {/* Back Button */}

      <button onClick={onBack} className="config-back-btn">

        <CloseOutlined />

        <span>Back to Products</span>

      </button>



      {/* Resource Info */}

      <div className="glass rounded-xl border border-white/10 mb-3 config-header-card">

        <div className="flex items-center justify-between config-header-content">

          <div className="config-header-left">

             <div className="config-header-meta">

              <span className="config-header-chip">{resourceTypeLabel}</span>

            </div>

            <h2 className="text-xl font-semibold text-white config-header-title">{resource.name}</h2>

          </div>

        </div>

      </div>



      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 config-layout">

        {/* Options Column */}

        <div className="lg:col-span-2 space-y-4 config-options-panel config-options-col">

            {resource.type !== 'server' && (resource.options?.location ?? []).length > 0 && (

              <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                <h3 className="font-semibold mb-3 text-white">Location</h3>

                {renderOptionSelect('location', 'Location', resource.options?.location ?? [])}

              </div>

            )}



                {resource.type !== 'server' && (resource.options?.vcpu?.length ?? 0) > 0 && (

                  <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                    <h3 className="font-semibold mb-3 text-white">CPU and RAM</h3>

                    {renderOptionSelect('vcpu', 'CPU and RAM', resource.options.vcpu)}

                  </div>

                )}



                {resource.type !== 'server' && resource.options?.gpu?.length > 0 && (

                  <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                    <h3 className="font-semibold mb-3 text-white">Streaming Quality</h3>

                    {renderOptionSelect('gpu', 'Streaming quality', resource.options.gpu)}

                  </div>

                )}



                {resource.type === 'server' && resource.options?.vcpu?.length > 0 && (

                  <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                    <h3 className="font-semibold mb-3 text-white">vCPU</h3>

                    {renderOptionSelect('vcpu', 'vCPU', resource.options.vcpu)}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 config-option-grid">

                      {resource.options.vcpu?.map(option => (

                        <button

                          key={option.id}

                          onClick={() => handleOptionChange('vcpu', option.value)}

                          disabled={!resource.available}

                          className={`p-3 rounded-lg border text-left transition-all config-option-btn ${

                            !resource.available

                              ? 'bg-gray-900/50 border-white/10 opacity-50 cursor-not-allowed is-disabled'

                              : selectedOptions.vcpu === option.value

                              ? 'bg-[#9FF13E]/10 border-[#9FF13E]/50 is-selected'

                              : 'glass-hover border-white/10'

                          }`}

                        >

                          <div className="config-option-line">

                            <span className="config-option-name">{stripOptionPrefix('vcpu', option.name)}</span>

                            <span className={`config-option-price ${option.is_free ? 'is-free' : ''}`}>

                              {formatOptionDelta(option)}

                            </span>

                          </div>

                        </button>

                      ))}

                    </div>

                  </div>

                )}



                {resource.type === 'server' && resource.options?.ram?.length > 0 && (

                  <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                    <h3 className="font-semibold mb-3 text-white">RAM</h3>

                    {renderOptionSelect('ram', 'RAM', resource.options.ram)}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 config-option-grid">

                      {resource.options.ram?.map(option => (

                        <button

                          key={option.id}

                          onClick={() => handleOptionChange('ram', option.value)}

                          disabled={!resource.available}

                          className={`p-3 rounded-lg border text-left transition-all config-option-btn ${

                            !resource.available

                              ? 'bg-gray-900/50 border-white/10 opacity-50 cursor-not-allowed is-disabled'

                              : selectedOptions.ram === option.value

                              ? 'bg-[#9FF13E]/10 border-[#9FF13E]/50 is-selected'

                              : 'glass-hover border-white/10'

                          }`}

                        >

                          <div className="config-option-line">

                            <span className="config-option-name">{stripOptionPrefix('ram', option.name)}</span>

                            <span className={`config-option-price ${option.is_free ? 'is-free' : ''}`}>

                              {formatOptionDelta(option)}

                            </span>

                          </div>

                        </button>

                      ))}

                    </div>

                  </div>

                )}



            {resource.type !== 'server' && resource.options?.storage?.length > 0 && (

              <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                <h3 className="font-semibold mb-3 text-white">Storage</h3>

                {renderOptionSelect('storage', 'Storage', resource.options.storage)}

              </div>

            )}



            {resource.type !== 'server' && resource.options?.os?.length > 0 && (

              <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

                <h3 className="font-semibold mb-3 text-white">Operating System</h3>

                {renderOptionSelect('os', 'OS', resource.options.os)}

                <div className="text-xs text-gray-400 mt-2">Uses ~40GB disk space</div>

                {isWindowsByolSelected() && (

                  <div className="license-check mt-3">

                    <Checkbox

                      checked={licenseAccepted}

                      onChange={(e) => setLicenseAccepted(e.target.checked)}

                    >

                      I agree that I am solely responsible for the compliance and management of my own Windows 10 license.

                    </Checkbox>

                  </div>

                )}

              </div>

            )}



          {resource.type === 'server' && (resource.options?.networking?.length ?? 0) > 0 && (

            <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

              <h3 className="font-semibold mb-3 text-white">Networking</h3>

              {renderOptionSelect('networking', 'Networking', resource.options.networking || [])}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 config-option-grid">

                {(resource.options.networking || []).map(option => (

                  <button

                    key={option.id}

                    onClick={() => handleOptionChange('networking', option.value)}

                    disabled={!resource.available}

                    className={`p-3 rounded-lg border text-left transition-all config-option-btn ${

                      !resource.available

                        ? 'bg-gray-900/50 border-white/10 opacity-50 cursor-not-allowed is-disabled'

                        : selectedOptions.networking === option.value

                        ? 'bg-[#9FF13E]/10 border-[#9FF13E]/50 is-selected'

                        : 'glass-hover border-white/10'

                    }`}

                  >

                    <div className="config-option-line">

                      <span className="config-option-name">{stripOptionPrefix('networking', option.name)}</span>

                      <span className={`config-option-price ${option.is_free ? 'is-free' : ''}`}>

                        {formatOptionDelta(option)}

                      </span>

                    </div>

                  </button>

                ))}

              </div>

            </div>

          )}



          {resource.type === 'server' && (resource.options?.system_disk?.length ?? 0) > 0 && (

            <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

              <h3 className="font-semibold mb-3 text-white">System Disk</h3>

              {renderOptionSelect('system_disk', 'System Disk', resource.options.system_disk || [])}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 config-option-grid">

                {(resource.options.system_disk || []).map(option => (

                  <button

                    key={option.id}

                    onClick={() => handleOptionChange('system_disk', option.value)}

                    disabled={!resource.available}

                    className={`p-3 rounded-lg border text-left transition-all config-option-btn ${

                      !resource.available

                        ? 'bg-gray-900/50 border-white/10 opacity-50 cursor-not-allowed is-disabled'

                        : selectedOptions.system_disk === option.value

                        ? 'bg-[#9FF13E]/10 border-[#9FF13E]/50 is-selected'

                        : 'glass-hover border-white/10'

                    }`}

                  >

                    <div className="config-option-line">

                      <span className="config-option-name">{stripOptionPrefix('system_disk', option.name)}</span>

                      <span className={`config-option-price ${option.is_free ? 'is-free' : ''}`}>

                        {formatOptionDelta(option)}

                      </span>

                    </div>

                  </button>

                ))}

              </div>

            </div>

          )}



          {resource.type === 'server' && (resource.options?.data_disk?.length ?? 0) > 0 && (

            <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

              <h3 className="font-semibold mb-3 text-white">Data Disk</h3>

              {renderOptionSelect('data_disk', 'Data Disk', resource.options.data_disk || [])}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 config-option-grid">

                {(resource.options.data_disk || []).map(option => (

                  <button

                    key={option.id}

                    onClick={() => handleOptionChange('data_disk', option.value)}

                    disabled={!resource.available}

                    className={`p-3 rounded-lg border text-left transition-all config-option-btn ${

                      !resource.available

                        ? 'bg-gray-900/50 border-white/10 opacity-50 cursor-not-allowed is-disabled'

                        : selectedOptions.data_disk === option.value

                        ? 'bg-[#9FF13E]/10 border-[#9FF13E]/50 is-selected'

                        : 'glass-hover border-white/10'

                    }`}

                  >

                    <div className="config-option-line">

                      <span className="config-option-name">{stripOptionPrefix('data_disk', option.name)}</span>

                      <span className={`config-option-price ${option.is_free ? 'is-free' : ''}`}>

                        {formatOptionDelta(option)}

                      </span>

                    </div>

                  </button>

                ))}

              </div>

            </div>

          )}



          {/* Time Selection */}

          <div className="glass rounded-xl border border-white/10 p-4 config-section-card">

            <h3 className="font-semibold mb-3 text-white">Duration</h3>

            <div className="grid grid-cols-2 gap-4 config-time-grid">

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Start Time</label>
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    value={startTime ? dayjs(startTime) : null}
                    onChange={(value) => {
                      if (!value) {
                        setStartTime('');
                        return;
                      }
                      const nextValue = value.format('YYYY-MM-DDTHH:mm');
                      setStartTime(clampStartTimeValue(nextValue, true));
                    }}
                    disabledDate={(current) => current && current.isBefore(dayjs(minStartDate), 'day')}
                    disabled={!resource.available}
                    className="settings-input w-full"
                  />
                  <span className="config-time-note">
                    Earliest start (local): {minStartLocalLabel}
                  </span>
                </div>
              <div>

                <label className="block text-sm text-gray-400 mt-3 mb-2">Duration</label>

                <div className="grid grid-cols-2 gap-2 config-duration-grid">

                  <div className="config-duration-field">

                    <input

                      type="number"

                      min={0}

                      max={720}

                      step={1}

                      value={durationHours}

                      onChange={(e) => setDurationHours(Math.max(0, Math.min(720, Number(e.target.value) || 0)))}

                      disabled={!resource.available}

                      className="w-full bg-gray-900/50 border border-white/10 rounded-lg px-4 py-2 text-white"

                      placeholder="Hours"

                    />

                    <span className="config-time-unit">hours</span>

                  </div>

                  <div className="config-duration-field">

                    <input

                      type="number"

                      min={0}

                      max={59}

                      step={1}

                      value={durationMinutes}

                      onChange={(e) => setDurationMinutes(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}

                      disabled={!resource.available}

                      className="w-full bg-gray-900/50 border border-white/10 rounded-lg px-4 py-2 text-white"

                      placeholder="Minutes"

                    />

                    <span className="config-time-unit">minutes</span>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>



        {/* Summary Sidebar */}

        <div className="lg:col-span-1 config-summary-col">

          <div className="glass rounded-xl border border-white/10 p-4 sticky top-4 config-summary-card">

            <h3 className="font-semibold mb-4 text-white">Configuration Summary</h3>



            {/* Selected Options */}

            <div className="space-y-2 mb-4 summary-list summary-section">

              {Object.entries(selectedOptions).map(([type, value]) => {

                if (!value) return null;

                const label = getCleanOptionLabel(type, value);

                return (

                  <div key={type} className="summary-item text-sm">

                    <span className="summary-key text-gray-400">{getOptionTypeLabel(type)}</span>

                    <span className="summary-value text-white">{label}</span>

                  </div>

                );

              })}

            </div>



            <div className="border-t border-white/10 space-y-2 summary-meta summary-section summary-meta-section">

              <div className="summary-item text-sm">

                <span className="summary-key text-gray-400">Base price</span>

                <span className="summary-value text-white">{formatFromEur(parseFloat(String(resource.base_price_per_hour || 0)))}/hr</span>

              </div>

              

              {/* Options price breakdown */}

              {Object.entries(selectedOptions).map(([type, value]) => {

                if (!value) return null;

                const label = getCleanOptionLabel(type, value);

                const { price } = getOptionPrice(type, value);

                if (price === 0) return null;

                return (

                  <div key={type} className="summary-item text-sm">

                    <span className="summary-key text-gray-400">{getOptionTypeLabel(type)}: {label}</span>

                    <span className="summary-value text-white">+{formatFromEur(price)}/hr</span>

                  </div>

                );

              })}



              <div className="summary-item text-sm">

                <span className="summary-key text-gray-400">Hourly rate</span>

                <span className="summary-value text-white">{formatFromEur(totalPrice)}/hr</span>

              </div>

              

              <div className="summary-item text-sm">

                <span className="summary-key text-gray-400">Duration</span>

                <span className="summary-value text-white">{durationHours}h {durationMinutes}m</span>

              </div>

              <div className="summary-vat-note">Prices include VAT</div>

            </div>



            <div className="border-t border-white/10 summary-section summary-total-section">

              <div className="summary-item text-lg font-semibold summary-total-row">

                <span className="summary-key text-white">Total</span>

                <span className="summary-value text-[#9FF13E]">{formatFromEur(parseFloat(calculateTotal()))}</span>

              </div>

            </div>



            <button

              onClick={handleOrder}

              disabled={processing || !resource.available}

              className={`btn btn-primary w-100 mt-4 config-pay-btn ${

                resource.available && !processing ? '' : 'is-disabled'

              }`}

            >

              {processing ? 'Processing...' : !resource.available ? 'Not Available' : 'Pay & Order'}

            </button>

          </div>

        </div>

      </div>

    </div>

  );

};



// ==================== Configurator Tab ====================

const ConfiguratorTab: React.FC<{ userTimezone: string; systemTimezone: string }> = ({ userTimezone, systemTimezone }) => {

  const [selectedResource, setSelectedResource] = useState<ResourceWithOptions | null>(null);



  const handleSelectResource = (resource: ResourceWithOptions) => {

    setSelectedResource(resource);

  };



  const handleBack = () => {

    setSelectedResource(null);

  };



  return (

    <div className="glass rounded-xl p-6 dashboard-create-shell">

      <h2 className="section-title mb-6">

        {selectedResource ? `Configure ${selectedResource.name}` : 'Create Your Machine'}

      </h2>

      

      {selectedResource ? (

        <MachineConfigurator resource={selectedResource} onBack={handleBack} userTimezone={userTimezone} systemTimezone={systemTimezone} />

      ) : (

        <ProductSelection onSelect={handleSelectResource} />

      )}

    </div>

  );

};



const Dashboard: React.FC = () => {

  const { user, logout, refreshUser } = useAuth();

  const [profileForm] = Form.useForm();

  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState<Order[]>([]);

  const [ordersPage, setOrdersPage] = useState(1);

  const ordersPageSize = 10;

  const [passwordLoading, setPasswordLoading] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const { formatFromEur } = useCurrency();

  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    setPageMeta({
      title: 'Dashboard | Clouniq',
      description: 'Monitor resources, manage orders, and control account settings in one dashboard.',
    });
  }, []);



  const fetchData = useCallback(async () => {
    try {
      const ordersUrl = user?.role === 'admin' && user?.id
        ? `/api/orders/user/${user.id}`
        : '/api/orders';
      const ordersRes = await api.get<{ data: Order[] }>(ordersUrl);
      setOrders(Array.isArray(ordersRes.data.data) ? ordersRes.data.data : []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  }, [user?.id, user?.role]);


  useEffect(() => {

    fetchData();

  }, [fetchData]);



  useEffect(() => {

    profileForm.setFieldsValue({

      firstName: user?.firstName || '',

      lastName: user?.lastName || '',

      email: user?.email || '',

      timezone: user?.timezone || userTimezone,

    });

  }, [profileForm, user, userTimezone]);



  const handleLogout = async () => {

    await logout();

  };



  const activeOrdersCount = useMemo(

    () => orders.filter((o: Order) => o.status === 'active').length,

    [orders],

  );

  const pendingOrdersCount = useMemo(

    () => orders.filter((o: Order) => o.status === 'pending').length,

    [orders],

  );

  const completedOrdersCount = useMemo(

    () => orders.filter((o: Order) => o.status === 'completed').length,

    [orders],

  );

  const totalOrdersCount = useMemo(

    () => orders.length,

    [orders],

  );

  const onTabChange = (tabKey: string) => {

    const next = new URLSearchParams(searchParams);

    next.set('tab', tabKey);

    setSearchParams(next, { replace: true });

  };



  useEffect(() => {

    setMobileMenuOpen(false);

  }, [activeTab]);

  const dashboardNavItems = [

    { key: 'overview', label: 'Overview', icon: <DashboardOutlined /> },

    { key: 'create', label: 'Create Machine', icon: <SettingOutlined /> },

    { key: 'orders', label: 'Order History', icon: <HistoryOutlined /> },

    { key: 'profile', label: 'Profile', icon: <UserOutlined /> },

  ];



  const pagedOrders = useMemo(() => {

    const start = (ordersPage - 1) * ordersPageSize;

    return orders.slice(start, start + ordersPageSize);

  }, [orders, ordersPage, ordersPageSize]);



  return (

    <div className="dashboard-page workspace-page">

      {/* Animated Background */}

      <div className="bg-animation">

        <div className="bg-mesh"></div>

        <div className="bg-grid"></div>

        <div className="bg-glow bg-glow-1"></div>

        <div className="bg-glow bg-glow-2"></div>

        <div className="bg-glow bg-glow-3"></div>

      </div>



      <div className="dashboard-command-wrap container">
      <div className="dashboard-hero workspace-shell dashboard-command-center">

        <div className="dashboard-command-main">

          <div className="dashboard-command-kicker">Workspace Command Center</div>

          <h1 className="hero-title">

            Build faster, <span className="gradient-text">{user?.name?.split(' ')[0] || 'Operator'}</span>

          </h1>

          <p className="hero-subtitle">Track machine lifecycle, monitor capacity signals, and move from request to launch in one flow.</p>

          <div className="dashboard-command-pills">
            <span>{activeOrdersCount} active workloads</span>
            <span>{pendingOrdersCount} waiting approval</span>
            <span>{totalOrdersCount} total orders</span>
          </div>

        </div>

        <div className="dashboard-command-side">
          <div className="dashboard-command-side-title">Session Snapshot</div>
          <div className="dashboard-command-side-grid">
            <article>
              <small>Completed</small>
              <strong>{completedOrdersCount}</strong>
            </article>
            <article>
              <small>Open tickets</small>
              <strong>{pendingOrdersCount > 0 ? pendingOrdersCount : 0}</strong>
            </article>
            <article>
              <small>Timezone</small>
              <strong>{userTimezone || 'UTC'}</strong>
            </article>
            <article>
              <small>Profile</small>
              <strong>{user?.email || 'Connected'}</strong>
            </article>
          </div>
        </div>

      </div>
      </div>



      <div className="dashboard-container container">

        <div className="dashboard-shell">

          <aside className="dashboard-side-menu glass">

            <div className="dashboard-side-top">

              <div className="dashboard-side-title">Dashboard</div>

              <button

                type="button"

                className="dashboard-side-toggle"

                onClick={() => setMobileMenuOpen((prev) => !prev)}

                aria-expanded={mobileMenuOpen}

              >

                {mobileMenuOpen ? 'Close' : 'Menu'}

              </button>

            </div>

            <div className={`dashboard-side-list ${mobileMenuOpen ? 'open' : ''}`}>

              {dashboardNavItems.map((item) => (

                <button

                  key={item.key}

                  type="button"

                  onClick={() => {

                    onTabChange(item.key);

                    setMobileMenuOpen(false);

                  }}

                  className={`dashboard-side-item ${activeTab === item.key ? 'active' : ''}`}

                >

                  <span className="dashboard-side-icon">{item.icon}</span>

                  <span>{item.label}</span>

                </button>

              ))}

            </div>

          </aside>



          <div className="dashboard-shell-content">

            <Tabs

              activeKey={activeTab}

              onChange={onTabChange}

              className="dashboard-tabs dashboard-tabs-sidebar"

              destroyInactiveTabPane

              animated={false}

              tabBarStyle={{ display: 'none' }}

            >

          <TabPane tab={<span><DashboardOutlined /> Overview</span>} key="overview">

            <section className="dashboard-signal-grid dashboard-section">
              <article className="dashboard-signal-card glass">
                <div className="metric-icon success"><CheckCircleOutlined /></div>
                <small>Active Orders</small>
                <strong>{activeOrdersCount}</strong>
              </article>
              <article className="dashboard-signal-card glass">
                <div className="metric-icon secondary"><ClockCircleOutlined /></div>
                <small>Pending Orders</small>
                <strong>{pendingOrdersCount}</strong>
              </article>
              <article className="dashboard-signal-card glass">
                <div className="metric-icon"><HistoryOutlined /></div>
                <small>Completed Orders</small>
                <strong>{completedOrdersCount}</strong>
              </article>
              <article className="dashboard-signal-card glass">
                <div className="metric-icon"><DashboardOutlined /></div>
                <small>Total Orders</small>
                <strong>{totalOrdersCount}</strong>
              </article>
            </section>

            <section className="dashboard-action-tiles dashboard-section">
              <Link to="/dashboard?tab=create" className="dashboard-action-tile glass glass-hover">
                <div className="action-icon"><PlusOutlined /></div>
                <div className="action-copy">
                  <span>Design a New Machine</span>
                  <small>Open configurator with modern presets and scheduling.</small>
                </div>
              </Link>

              <Link to="/dashboard?tab=orders" className="dashboard-action-tile glass glass-hover">
                <div className="action-icon"><HistoryOutlined /></div>
                <div className="action-copy">
                  <span>Audit Order Timeline</span>
                  <small>Inspect statuses, payments, and component breakdowns.</small>
                </div>
              </Link>

              <Link to="/dashboard?tab=profile" className="dashboard-action-tile glass glass-hover">
                <div className="action-icon"><UserOutlined /></div>
                <div className="action-copy">
                  <span>Adjust Account Settings</span>
                  <small>Update identity data, timezone, and login security.</small>
                </div>
              </Link>
            </section>

          </TabPane>

          <TabPane tab={<span><SettingOutlined /> Create Machine</span>} key="create">

            <ConfiguratorTab userTimezone={userTimezone} systemTimezone={user?.system_timezone || 'UTC'} />

          </TabPane>

          <TabPane tab={<span><HistoryOutlined /> Order History</span>} key="orders">

            <Card className="glass dashboard-orders-card">

              {orders.length === 0 ? (

                <div className="empty-state">

                  <div className="empty-state-title text-white">No orders yet</div>

                  <div className="empty-state-subtitle text-white">Once you place your first order, it will appear here.</div>

                </div>

              ) : (

                <div className="dashboard-orders-cards compact-orders">

                  {pagedOrders.map((order) => {

                    const resourceName =

                      order.resource?.name ||

                      (order as unknown as { Resource?: Resource; resource_name?: string }).Resource?.name ||

                      (order as unknown as { resource_name?: string }).resource_name ||

                      'N/A';

                    const startLabel = order.start_time ? formatDateInTimezone(order.start_time, userTimezone) : '—';

                    const endLabel = order.end_time ? formatDateInTimezone(order.end_time, userTimezone) : '—';

                    const totalValue = parseFloat(String(order.total_cost || 0));
                    const orderCurrency =
                      (order as unknown as { currency?: string; order_currency?: string }).currency
                      || (order as unknown as { order_currency?: string }).order_currency;
                    const totalLabel = orderCurrency
                      ? formatMoney(totalValue, orderCurrency)
                      : formatFromEur(totalValue);

                    return (

                      <details key={order.id} className="order-row glass">

                        <summary className="order-row-summary">

                          <div className="order-row-id-block">
                            <div className="order-row-id">#{order.order_number || 'N/A'}</div>
                            {order?.payment?.payNetOrderId && (
                              <div className="order-row-paynet">Paynet ID: {order.payment.payNetOrderId}</div>
                            )}
                          </div>

                          <div className="order-row-main">

                            <span className="order-row-resource">{resourceName}</span>

                            <span className="order-row-dates">{startLabel} - {endLabel}</span>

                          </div>

                          <div className="order-row-meta">

                            <Tag

                              color={

                                order.status === 'active'

                                  ? 'green'

                                  : order.status === 'completed'

                                  ? 'blue'

                                  : order.status === 'cancelled'

                                  ? 'red'

                                  : 'orange'

                              }

                            >

                              {order.status?.charAt(0).toUpperCase() + order.status?.slice(1) || 'Unknown'}

                            </Tag>
                            {(() => {
                              const paymentStatus = order.payment_status || 'payment_awaited';
                              const color =
                                paymentStatus === 'paid'
                                  ? 'green'
                                  : paymentStatus === 'payment_failed'
                                  ? 'red'
                                  : 'orange';
                              const label =
                                paymentStatus === 'paid'
                                  ? 'Paid'
                                  : paymentStatus === 'payment_failed'
                                  ? 'Payment failed'
                                  : 'Payment awaited';
                              return <Tag color={color}>{label}</Tag>;
                            })()}

                            <span className="order-row-total">{totalLabel}</span>

                          </div>

                        </summary>

                        <div className="order-row-body">

                          <div className="order-row-section">

                            <span className="order-row-label">Components</span>

                            <div className="order-components">

                              {(order.items || []).length ? (

                                order.items?.map((item) => {

                                  const normalize = (val: string) => (val || '').toString().trim().toLowerCase();

                                  const shouldHideValue = (name?: string, value?: string) => {

                                    if (!name || !value) return true;

                                    const n = normalize(name);

                                    const v = normalize(value);

                                    return n === v || n.endsWith(v) || n.includes(` ${v}`);

                                  };

                                  return (

                                    <div key={item.id} className="order-component-pill">

                                      <span className="order-component-name">{item.name}</span>

                                      {!shouldHideValue(item.name, item.value) && (

                                        <>

                                          <span className="order-component-sep">:</span>

                                          <span className="order-component-value">{item.value}</span>

                                        </>

                                      )}

                                    </div>

                                  );

                                })

                              ) : (

                                <span className="text-gray-500">-</span>

                              )}

                            </div>

                          </div>

                        </div>

                      </details>

                    );

                  })}

                </div>

              )}



              {orders.length > ordersPageSize && (

                <div className="orders-pagination">

                  <button

                    className="orders-page-btn"

                    onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}

                    disabled={ordersPage === 1}

                    aria-label="Previous page"

                  >

                    {'<'}

                  </button>

                  <span className="orders-page-info">

                    {ordersPage} / {Math.max(1, Math.ceil(orders.length / ordersPageSize))}

                  </span>

                  <button

                    className="orders-page-btn"

                    onClick={() => setOrdersPage((p) => Math.min(Math.ceil(orders.length / ordersPageSize), p + 1))}

                    disabled={ordersPage >= Math.ceil(orders.length / ordersPageSize)}

                    aria-label="Next page"

                  >

                    {'>'}

                  </button>

                </div>

              )}

            </Card>

          </TabPane>

          <TabPane tab={<span><UserOutlined /> Profile</span>} key="profile">

            <Row gutter={[24, 24]} className="dashboard-section dashboard-profile-row">

              <Col xs={24} lg={24}>

                <Card className="glass dashboard-profile-form-card">

                  <AntTitle level={4} className="dashboard-profile-section-title">Personal Information</AntTitle>

                  <Form

                    form={profileForm}

                    layout="vertical"

                    onFinish={async (values) => {

                      try {

                        await api.put('/api/users/me', {

                          firstName: values.firstName,

                          lastName: values.lastName,

                          timezone: values.timezone,

                        });

                        await refreshUser();

                        notification.success({ message: 'Profile updated successfully' });

                      } catch (err) {

                        notification.error({ message: 'Failed to update profile' });

                      }

                    }}

                  >

                    <Row gutter={16}>

                      <Col xs={24} sm={12}>

                        <Form.Item name="firstName" label={<span className="dashboard-form-label">First Name</span>}>

                          <Input prefix={<UserOutlined />} />

                        </Form.Item>

                      </Col>

                      <Col xs={24} sm={12}>

                        <Form.Item name="lastName" label={<span className="dashboard-form-label">Last Name</span>}>

                          <Input prefix={<UserOutlined />} />

                        </Form.Item>

                      </Col>

                    </Row>

                    <Form.Item name="email" label={<span className="dashboard-form-label">Email</span>}>

                      <Input disabled />

                    </Form.Item>

                    <Form.Item

                      name="timezone"

                      label={<span className="dashboard-form-label">Timezone</span>}

                      rules={[{ required: true, message: 'Please select your timezone' }]}

                    >

                      <Select

                        showSearch={false}

                        showArrow={false}

                        optionFilterProp="label"

                        className="dashboard-timezone-select"

                        popupClassName="dashboard-timezone-dropdown"

                        options={timezoneOptions.map((tz: string) => ({ value: tz, label: tz }))}

                      />

                    </Form.Item>

                    <Form.Item className="mt-4">

                      <Space className="profile-form-actions">

                        <Button type="primary" htmlType="submit" className="dashboard-form-primary-btn">

                          Save Changes

                        </Button>

                        <Button onClick={handleLogout} className="dashboard-form-secondary-btn">

                          Logout

                        </Button>

                      </Space>

                    </Form.Item>

                  </Form>



                  <div className="profile-password-divider"></div>

                  <AntTitle level={5} className="dashboard-profile-section-title">Change Password</AntTitle>

                  <Form

                    layout="vertical"

                    onFinish={async (values) => {

                      if (values.newPassword !== values.confirmPassword) {

                        notification.error({ message: 'New passwords do not match' });

                        return;

                      }



                      setPasswordLoading(true);

                      try {

                        await api.put('/api/users/me/password', {

                          oldPassword: values.oldPassword,

                          newPassword: values.newPassword,

                        });

                        notification.success({ message: 'Password updated successfully' });

                      } catch (err: any) {

                        notification.error({ message: err?.response?.data?.message || 'Failed to update password' });

                      } finally {

                        setPasswordLoading(false);

                      }

                    }}

                  >

                    <Row gutter={16}>

                      <Col xs={24}>

                        <Form.Item

                          name="oldPassword"

                          label={<span className="dashboard-form-label">Current Password</span>}

                          rules={[{ required: true, message: 'Please enter your current password' }]}

                        >

                          <Input.Password />

                        </Form.Item>

                      </Col>

                      <Col xs={24} sm={12}>

                        <Form.Item

                          name="newPassword"

                          label={<span className="dashboard-form-label">New Password</span>}

                          rules={[{ required: true, message: 'Please enter a new password' }]}

                        >

                          <Input.Password />

                        </Form.Item>

                      </Col>

                      <Col xs={24} sm={12}>

                        <Form.Item

                          name="confirmPassword"

                          label={<span className="dashboard-form-label">Confirm New Password</span>}

                          rules={[{ required: true, message: 'Please confirm your new password' }]}

                        >

                          <Input.Password />

                        </Form.Item>

                      </Col>

                    </Row>

                    <Form.Item>

                      <Button type="primary" htmlType="submit" loading={passwordLoading} className="dashboard-form-primary-btn">

                        Update Password

                      </Button>

                    </Form.Item>

                  </Form>

                </Card>

              </Col>

            </Row>

          </TabPane>

            </Tabs>

          </div>

        </div>

      </div>

    </div>

  );

};



export default Dashboard;





